import { ActiveParty, isActiveParty } from '../Model/Types/ActiveParty';
import {
  ERROR_CODE,
  IInteraction,
  LOG_LEVEL,
} from '@amc-technology/davinci-api';

import { AmcCall } from '../Model/Classes/AmcCall';
import { ISoftPhoneState } from '../Model/Interfaces/ISoftPhoneState';
import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Subscription } from 'rxjs';
import { saturateAmcCall } from '../Model/Classes/ScvObjectSaturation';

@Injectable()
export class StorageService {
  log: (
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any,
    errorCode?: ERROR_CODE,
    localTime?: Date
  ) => void;

  private activeCalls: AmcCall[] = [];
  private activeParties: ActiveParty[] = [];

  stateSubscriber?: Subscription;
  interactionToVoiceCallRecordDetailsMap: Map<
    string,
    {
      voiceCallId: string;
      startTime: Date;
    }
  > = new Map([]);

  constructor(private readonly loggerService: LoggerService) {
    this.log = loggerService.log.bind(loggerService);
    this.syncWithLocalStorage();
  }

  updateActiveCalls(activeCalls: AmcCall[]): void {
    const functionName = 'SS.' + 'updateActiveCalls';
    try {
      this.activeCalls = activeCalls;
      this.storeToLocalStorage();
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error updating activeCalls', error);
    }
  }

  getActiveCalls(): AmcCall[] {
    const functionName = 'SS.' + 'getActiveCalls';
    try {
      this.log(LOG_LEVEL.Loop, functionName, 'Getting activeCalls', this.activeCalls);
      return this.activeCalls;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error getting activeCalls', error);
      return [];
    }
  }

  updateActiveParties(activeParties: ActiveParty[]): void {
    const functionName = 'SS.' + 'updateActiveParties';
    try {
      this.activeParties = activeParties;
      this.storeToLocalStorage();
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error updating activeParties', error);
    }
  }

  getActiveParties(): ActiveParty[] {
    const functionName = 'SS.' + 'getActiveParties';
    try {
      this.log(LOG_LEVEL.Loop, functionName, 'Getting activeParties', this.activeParties);
      return this.activeParties;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error getting activeParties', error);
      return [];
    }
  }


  storeToLocalStorage(): void {
    const functionName = 'SS.' + 'storeToLocalStorage';
    try {
      localStorage.setItem('AMC_ACTIVE_CALLS', this.hexEncode(JSON.stringify(this.activeCalls)));
      localStorage.setItem('AMC_ACTIVE_PARTIES', this.hexEncode(JSON.stringify(this.activeParties)));
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Error storing to local storage',
        error
      );
    }
  }

  private syncWithLocalStorage(): void {
    const functionName = 'SS.' + 'syncWithLocalStorage';
    try {

      const activeCallsRaw = this.hexDecode(localStorage.getItem('AMC_ACTIVE_CALLS') ?? '');
      const activePartiesRaw = this.hexDecode(localStorage.getItem('AMC_ACTIVE_PARTIES') ?? '');

      if (activeCallsRaw) {
        const activeCallsRawArr = JSON.parse(activeCallsRaw);
        if (Array.isArray(activeCallsRawArr)) {
          this.activeCalls.length = 0;

          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          activeCallsRawArr.forEach(callRaw => this.activeCalls.push(saturateAmcCall(callRaw as AmcCall).saturated));
        }
      }

      if (activePartiesRaw) {
        const activePartiesRawArr = JSON.parse(activePartiesRaw);
        if (Array.isArray(activePartiesRawArr)) {
          this.activeParties.length = 0;

          activePartiesRawArr.forEach(partyRaw => {
            const isAnActiveParty = isActiveParty(partyRaw);

            if (isAnActiveParty) {
              this.activeParties.push(partyRaw);
            } else {
              throw new Error('Invalid active party data: ' + JSON.stringify(partyRaw));
            }
          });
        }
      }
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Error syncing with local storage',
        error
      );
    }
  }


  getCurrentState(): object | undefined {
    const functionName = 'SS.' + 'getCurrentState';
    try {
      const state = JSON.parse(localStorage.getItem('state') || '');
      this.log(LOG_LEVEL.Loop, functionName, 'Current state', state);
      // TODO: Fix this
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return state;
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Error getting current state',
        error
      );
    }
    return;
  }

  setState(state: ISoftPhoneState): void {
    const functionName = 'SS.' + 'setState';
    try {
      this.log(LOG_LEVEL.Loop, functionName, 'Setting state', state);
      console.log(`STORAGE CALL DATA:\n${JSON.stringify(state, null, 2)}`);
      localStorage.setItem('state', JSON.stringify(state));
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error setting state', error);
    }
  }

  hexEncode(str: string): string {
    return Array.from(str)
      .map(c => c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('');
  }

  hexDecode(hex: string): string {
    if (hex.length % 2 !== 0) {
      throw new Error('Invalid hex string');
    }
    let result = '';
    for (let i = 0; i < hex.length; i += 2) {
      result += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
    }
    return result;
  }
}
